import {
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from 'firebase/auth';
import { auth } from '../services/firebaseConfig';
import { User } from '../models/User';

const provider = new GoogleAuthProvider();
// Configure Google provider for Gmail support
provider.setCustomParameters({
  prompt: 'select_account',
  // The client ID is defined in Firebase console and doesn't need to be added here
  // It will use the OAuth client ID configured in Firebase project
});

export const signInWithGoogle = async (): Promise<User | null> => {
  try {
    // Set auth persistence to LOCAL
    await setPersistence(auth, browserLocalPersistence);

    const result = await signInWithPopup(auth, provider).catch(error => {
      if (error.code === 'auth/popup-blocked') {
        // If popup is blocked, try again with session persistence
        return setPersistence(auth, browserSessionPersistence).then(() =>
          signInWithPopup(auth, provider)
        );
      }
      throw error;
    });

    const user = result.user;
    if (!user) {
      throw new Error('No user data received');
    }

    // Get a fresh token
    const token = await user.getIdToken(true);

    // Create user object
    const userData: User = {
      id: user.uid,
      username: user.displayName || 'Google User',
      email: user.email || '',
      avatar: user.photoURL || '',
      accessToken: token,
    };

    // Note: Token storage now handled by Redux persistence for security
    // No longer storing sensitive tokens in localStorage to prevent XSS vulnerabilities

    return userData;
  } catch (error) {
    // Handle specific error cases
    if (error instanceof Error) {
      switch (error.name) {
        case 'PopupBlockedError':
          console.error('Popup was blocked by the browser');
          throw new Error('Please enable popups for this site to sign in with Google');

        case 'FirebaseError':
          if ((error as any).code === 'auth/popup-closed-by-user') {
            throw new Error('Sign-in cancelled by user');
          }
          if ((error as any).code === 'auth/cancelled-popup-request') {
            throw new Error('Another sign-in attempt is in progress');
          }
          break;

        default:
          console.error('Sign-in error:', error);
      }
    }

    throw new Error('Failed to sign in with Google');
  }
};

export const logOut = async (): Promise<void> => {
  try {
    await signOut(auth);
    // Note: Auth data cleanup now handled by Redux state management
    // No longer using localStorage for sensitive token storage
  } catch (error) {
    console.error('Logout error:', error);
    throw new Error('Failed to sign out');
  }
};

// Helper to check if user is authenticated using Firebase auth state
export const checkAuthStatus = (): boolean => {
  // Use Firebase auth current user instead of localStorage
  const currentUser = auth.currentUser;
  return !!currentUser;
};

// Helper to get current Firebase token (async)
export const getCurrentFirebaseToken = async (): Promise<string | null> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return null;
    
    // Get fresh token from Firebase (automatically refreshed if needed)
    return await currentUser.getIdToken();
  } catch (error) {
    console.error('Error getting Firebase token:', error);
    return null;
  }
};
